import { comparer, type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { ZIndex } from 'shared/constants/zIndex'
import type { IResponseContact } from 'entities/Contacts/api/types'
import { type Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactInfoFormStore } from 'widgets/ContactsInfoForm'
import { ContactsIntegrationsStore } from 'widgets/ContactsDetails/ui/ContactsIntegrations/store/contactsIntegrationsStore'
import { CallPopUpCallInsightCardsCallTypeStore } from 'widgets/CallPopUp/store/callPopUpCallInsightCardsCallTypeStore'
import type { IViewTypes } from 'widgets/ContactsDetails'
import { CallNoteStore } from 'widgets/CallPopUp/ui/CallInsightCards/CallInsightCardsNoteForm'

export class CallPopUpInsightCardsStore {
  private _activeTab = 'details'
  private _view: IViewTypes = 'main'
  private _loading = false
  private _contactId: number | null = null
  private _currentContact: Contact | null = null
  private readonly _contactIntegrationsStore: ContactsIntegrationsStore
  private _callNoteStore = new CallNoteStore(this)
  private readonly _contactInfoFormStore: ContactInfoFormStore
  private _showContent = false
  private _disposeExternalContactUpdated: IReactionDisposer | null = null
  private _disposeCallClosed: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._view = 'main'
    this._contactInfoFormStore = new ContactInfoFormStore(this._entities)
    this._contactIntegrationsStore = new ContactsIntegrationsStore(this._entities)

    this._contactIntegrationsStore.setConfig({
      onOpenView: (value) => {
        this.openView(value)
      },
      onCloseView: () => {
        this.closeView()
      },
      ZIndex: ZIndex.OVERLAY_TOP_1,
      blockKeyView: 'callInsightIntegration',
    })

    this._contactInfoFormStore.setConfig({
      handleUpdateContact: this.handleUpdateContact,
      variantView: 'callPopUp',
    })
    this.reactionExternalContactUpdated()
    this.reactionCallClosed()
  }

  get entities() {
    return this._entities
  }

  get callStore() {
    return this._entities.callStore
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  syncActiveCallNote = (callSid: string, contactId: number, teamId: number, entryId: string) => {
    this._callNoteStore.syncActiveCall(callSid, contactId, teamId, entryId)
    this._activeTab = 'details'
  }

  reactionCallClosed = () => {
    this._disposeCallClosed?.()
    this._disposeCallClosed = reaction(
      () => Array.from(this.callStore.callsMap.keys()),
      (sids, prevSids) => {
        const removed = (prevSids ?? []).filter((sid) => !sids.includes(sid))
        removed.forEach((sid) => this._callNoteStore.sendDraftForCallSid(sid))
      },
      { equals: comparer.structural }
    )
  }

  reactionExternalContactUpdated = () => {
    this._disposeExternalContactUpdated?.()
    this._disposeExternalContactUpdated = reaction(
      () => this._entities.contactsStore.getItem(this._contactId ?? undefined),
      (contact) => {
        if (contact) {
          this._currentContact = contact
          this._contactIntegrationsStore.setContactId(contact.id, contact)
          this.contactInfoFormStore.syncFields(contact.id)
          if (!this._callPopUpCallInsightCardsCallTypeStore) {
            this._callPopUpCallInsightCardsCallTypeStore =
              new CallPopUpCallInsightCardsCallTypeStore()
          }
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  handleUpdateContact = (item: IResponseContact) => {
    this._entities.contactsStore.updateItem(item)

    const contact = this._entities.contactsStore.getItem(item.id)

    if (contact) {
      this._contactId = contact.id
      this._currentContact = contact
    }
  }

  get contact() {
    if (this._contactId === null) return
    return this._entities.contactsStore.getItem(this._contactId) || this._currentContact
  }

  private _callPopUpCallInsightCardsCallTypeStore: CallPopUpCallInsightCardsCallTypeStore | null =
    null

  setContactId = (id: number, contact?: Contact) => {
    if (this._contactId === id) return

    this._contactId = id
    this._currentContact = contact ?? null

    this._contactInfoFormStore.setContactId(id, contact)
    this._contactIntegrationsStore.setContactId(id, contact)

    if (this.contact) {
      this._callPopUpCallInsightCardsCallTypeStore = new CallPopUpCallInsightCardsCallTypeStore()
    }
  }

  reset = () => {
    this._contactId = null
    this._currentContact = null
    this._activeTab = 'details'
    this._disposeExternalContactUpdated?.()
    this._disposeCallClosed?.()
  }

  setTabKey = (key: string) => {
    this._activeTab = key
    this.setNoteState()
  }

  openView = (viewType: IViewTypes) => {
    this._view = viewType
    this._contactIntegrationsStore.setView(viewType)
  }

  closeView = () => {
    this._view = 'main'
    this._contactIntegrationsStore.setView('main')
  }

  toggleShowContent = () => {
    this._showContent = !this._showContent
    this.setNoteState()
  }

  setNoteState = () => {
    if (!this.note || !this.isShowContent) return

    this._callNoteStore?.setFocusMessageField()
    this._callNoteStore?.setMessageFieldData()
  }

  setCallTypeModalState = (status: boolean) => {
    this.callPopUpCallInsightCardsCallTypeStore?.setModalStatus(status)
  }

  setLoadingState = (status: boolean) => {
    this._loading = status
  }

  get isShowContent() {
    return this._showContent
  }

  get view() {
    return this._view
  }

  get activeTab() {
    return this._activeTab
  }

  get titleHeader() {
    if (this._view === 'notes') return 'Notes'
    if (this._view === 'deals') return 'Deals'
    if (this._view === 'opportunities') return 'Opportunities'
    if (this._view === 'companies') return 'Companies'
    if (this._view === 'tickets') return 'Tickets'

    return null
  }

  get isViewMain() {
    return this._view === 'main'
  }

  get isViewAll() {
    if (this._view === 'main') return false

    return (
      this._view === 'notes' ||
      this._view === 'deals' ||
      this._view === 'opportunities' ||
      this._view === 'companies' ||
      this._view === 'tickets'
    )
  }

  get details() {
    return this.activeTab === 'details'
  }

  get note() {
    return this.activeTab === 'note'
  }

  get contactInfoFormStore() {
    return this._contactInfoFormStore
  }

  get callPopUpCallInsightCardsCallTypeStore() {
    return this._callPopUpCallInsightCardsCallTypeStore
  }

  get showCallTypesModal() {
    return this.callPopUpCallInsightCardsCallTypeStore?.showModal
  }

  get contactIntegrationsStore() {
    return this._contactIntegrationsStore
  }

  get callNoteStore() {
    return this._callNoteStore
  }

  get noteText() {
    return this._callNoteStore.activeDraftText
  }

  get loading() {
    return this._loading
  }
}
